import {EstimateTreeDto} from '../../../shared/dtos/estimate-tree.dto';
import {EstimateTreeOperationDto} from '../../../shared/dtos/estimate-tree-operation.dto';
import {operationLabel, WORK_ITEM_LABEL} from './operation-schema';
import {distribute, distributeDollars, laborMinutes} from './record-math';

/*
 * SQ-660 (ParentAdjust.kt): the work item's timer, the Total Labor Time & Cost
 * timer and the typed Job Total spread a change over the operations by their
 * prices, so they only work once every operation under them has one. A price
 * of $0 counts as none. And no change of theirs may take an operation to $0.
 */

export const operationPriced = (op: EstimateTreeOperationDto) => Math.round(op.estimatedCost ?? 0) > 0;

interface Named {op: EstimateTreeOperationDto; name: string}

/** A work item named as its card's header ("#2 Tree"), numbered as the cards are, by `allTrees`. */
const workItemName = (tree: EstimateTreeDto, allTrees: EstimateTreeDto[]) =>
  `#${allTrees.indexOf(tree) + 1} ${WORK_ITEM_LABEL[tree.workItemType] ?? tree.workItemType}`;

/** Every operation of `trees`, named "Thinning" in its work item, else "Thinning on #2 Tree". */
const named = (trees: EstimateTreeDto[], allTrees: EstimateTreeDto[], inWorkItem: boolean): Named[] =>
  trees.flatMap(tree => {
    const workItem = workItemName(tree, allTrees);
    return tree.operations.map(op => ({op, name: inWorkItem ? operationLabel(op.operationType) : `${operationLabel(op.operationType)} on ${workItem}`}));
  });

/** Every work item of `trees` has an operation, and every operation a price. */
export const allPriced = (trees: EstimateTreeDto[]) =>
  trees.every(t => t.operations.length > 0) && trees.flatMap(t => t.operations).every(operationPriced);

const joinNames = (names: string[]) => names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

/**
 * Why a parent control is locked, or undefined. First a work item with no
 * operation (nothing to put time on yet), then the operations still without
 * a price. `word` is what the person would add ("time" with a crew, else "a
 * price"). Up to three are named; more are counted, the first named.
 */
export const missingPriceReason = (trees: EstimateTreeDto[], allTrees: EstimateTreeDto[], inWorkItem: boolean, word: string) => {
  const empty = trees.filter(t => t.operations.length === 0).map(t => workItemName(t, allTrees));
  if (empty.length > 0) {
    return empty.length <= 3
      ? `Add an operation to ${joinNames(empty)} first.`
      : `${empty.length} work items have no operation yet. First: ${empty[0]}.`;
  }
  const missing = named(trees, allTrees, inWorkItem).filter(n => !operationPriced(n.op)).map(n => n.name);
  if (missing.length === 0) return undefined;
  return missing.length <= 3
    ? `Add ${word} to ${joinNames(missing)} first.`
    : `${missing.length} operations still need ${word}. First: ${missing[0]}.`;
};

const zeroReason = (name: string) => `That would take ${name} to zero.`;

/** A step of `deltaWall` on a parent timer, refused when it would leave an operation at $0 (useWorkItems' own split and pricing). */
export const parentStepRefusal = (
  trees: EstimateTreeDto[], allTrees: EstimateTreeDto[], inWorkItem: boolean, deltaWall: number, crewSize: number, rate?: number | null,
) => {
  if (deltaWall >= 0 || !rate || rate <= 0) return undefined;
  const ops = named(trees, allTrees, inWorkItem);
  if (ops.length === 0) return undefined;
  const current = ops.map(n => laborMinutes(n.op, rate));
  const split = distribute(current, Math.max(0, current.reduce((a, b) => a + b, 0) + deltaWall * crewSize));
  const zeroed = split.findIndex(minutes => Math.round(minutes / 60 * rate) <= 0);
  return zeroed < 0 ? undefined : zeroReason(ops[zeroed].name);
};

/** A typed Job Total leaving `laborTotal` for labor, refused when it would take an operation to $0. */
export const laborTotalRefusal = (trees: EstimateTreeDto[], laborTotal: number) => {
  const ops = named(trees, trees, false);
  if (ops.length === 0) return undefined;
  const prices = distributeDollars(ops.map(n => Math.round(n.op.estimatedCost ?? 0)), Math.max(0, Math.round(laborTotal)));
  const zeroed = prices.findIndex(p => p <= 0);
  return zeroed < 0 ? undefined : zeroReason(ops[zeroed].name);
};
